import { AnimatePresence, motion, type TargetAndTransition, type Transition } from 'framer-motion';
import { lazy, Suspense, useCallback, useEffect, useRef, type KeyboardEvent, type PointerEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { Bizy, type BizyAnimationOverrides, type BizySize, type BizyState } from '../../bizy';
import { useBizyGameEasterEgg } from '../hooks/use-bizy-game-easter-egg';
import { BizyGameInvite } from './bizy-game-invite';

const LazyBizySwarmGame = lazy(() => import('./bizy-swarm-game').then((module) => ({ default: module.BizySwarmGame })));

export interface BizyGameEasterEggTriggerProps {
	state: BizyState;
	size?: BizySize;
	onClick?: () => void;
	holdEnabled?: boolean;
	ariaLabel?: string;
	holdDisabledAriaLabel?: string;
	animations?: BizyAnimationOverrides;
	animate?: TargetAndTransition;
	transition?: Transition;
	anchorRef?: Ref<HTMLDivElement>;
	hidden?: boolean;
	/** Keep pointer hold active but skip tab focus (for decorative mascot surfaces). */
	excludeFromTabOrder?: boolean;
}

export function BizyGameEasterEggTrigger({
	state,
	size = 'md',
	onClick,
	holdEnabled = true,
	ariaLabel,
	holdDisabledAriaLabel,
	animations,
	animate,
	transition,
	anchorRef,
	hidden = false,
	excludeFromTabOrder = false,
}: BizyGameEasterEggTriggerProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const hasTrackedInviteShownRef = useRef(false);
	const holdCompletedRef = useRef(false);

	const {
		isPromptOpen: isGamePromptOpen,
		isGameOpen,
		holdProgress,
		isHolding,
		startBizyHold,
		cancelBizyHold,
		dismissPrompt: dismissGamePrompt,
		openGame,
		closeGame,
	} = useBizyGameEasterEgg();

	const holdSpinProgress = isHolding ? Math.max(0, (holdProgress - 0.2) / 0.8) : 0;
	const holdSpinAnimation = animate ?? { x: 0, y: 0, rotate: -360 * holdSpinProgress, scale: 1 };
	const holdSpinTransition = transition ?? { duration: isHolding ? 0.08 : 0.18, ease: 'linear' as const };

	const handleClick = useCallback(() => {
		if (holdCompletedRef.current) {
			holdCompletedRef.current = false;
			return;
		}
		onClick?.();
	}, [onClick]);

	const isInteractive = !hidden;

	const handlePointerDown = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			if (!holdEnabled || !isInteractive) return;
			event.currentTarget.setPointerCapture?.(event.pointerId);
			startBizyHold();
		},
		[holdEnabled, isInteractive, startBizyHold]
	);

	const handlePointerEnd = useCallback(() => {
		if (!isInteractive) return;
		cancelBizyHold();
	}, [cancelBizyHold, isInteractive]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (!isInteractive || (event.key !== 'Enter' && event.key !== ' ')) return;
			event.preventDefault();
			if (!holdEnabled) {
				onClick?.();
				return;
			}
			if (event.repeat) return;
			startBizyHold();
		},
		[holdEnabled, isInteractive, onClick, startBizyHold]
	);

	const handleKeyUp = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (!isInteractive || (event.key !== 'Enter' && event.key !== ' ')) return;
			event.preventDefault();
			if (holdEnabled) {
				if (!holdCompletedRef.current) {
					onClick?.();
				}
				cancelBizyHold();
			}
		},
		[cancelBizyHold, holdEnabled, isInteractive, onClick]
	);

	const handleGameInvitePlay = useCallback(() => {
		track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
			action: 'play',
			path: window.location.pathname,
		});
		holdCompletedRef.current = false;
		openGame();
	}, [openGame, track]);

	const handleGameInviteDismiss = useCallback(() => {
		track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
			action: 'dismiss',
			path: window.location.pathname,
		});
		holdCompletedRef.current = false;
		dismissGamePrompt();
	}, [dismissGamePrompt, track]);

	useEffect(() => {
		if (!isGamePromptOpen) return;

		const handleOutsidePointerDown = (event: globalThis.PointerEvent) => {
			if (event.target instanceof HTMLElement && event.target.closest('[data-bizy-game-invite]')) return;
			track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
				action: 'outside_dismiss',
				path: window.location.pathname,
			});
			holdCompletedRef.current = false;
			dismissGamePrompt();
		};

		document.addEventListener('pointerdown', handleOutsidePointerDown);
		return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
	}, [dismissGamePrompt, isGamePromptOpen, track]);

	useEffect(() => {
		if (!isGamePromptOpen || !holdEnabled) {
			hasTrackedInviteShownRef.current = false;
			return;
		}
		if (hasTrackedInviteShownRef.current) return;

		holdCompletedRef.current = true;
		hasTrackedInviteShownRef.current = true;
		track(AnalyticsEvent.Bizy.BizySkyHighInviteShown, {
			path: window.location.pathname,
		});
	}, [holdEnabled, isGamePromptOpen, track]);

	const resolvedAriaLabel = holdEnabled ? (ariaLabel ?? t('gameInvite.ariaLabel')) : (holdDisabledAriaLabel ?? ariaLabel ?? t('gameInvite.ariaLabel'));

	return (
		<>
			<motion.div
				ref={anchorRef}
				style={{ visibility: hidden ? 'hidden' : 'visible', position: 'relative', pointerEvents: hidden ? 'none' : undefined }}
				aria-hidden={hidden || undefined}
			>
				<motion.div animate={holdSpinAnimation} transition={holdSpinTransition} style={{ display: 'flex' }}>
					<motion.div
						onClick={isInteractive ? handleClick : undefined}
						onPointerDown={isInteractive ? handlePointerDown : undefined}
						onPointerUp={isInteractive ? handlePointerEnd : undefined}
						onPointerCancel={isInteractive ? handlePointerEnd : undefined}
						onKeyDown={isInteractive && !excludeFromTabOrder ? handleKeyDown : undefined}
						onKeyUp={isInteractive && !excludeFromTabOrder ? handleKeyUp : undefined}
						style={{ cursor: isInteractive ? 'pointer' : 'default', display: 'flex' }}
						role={isInteractive ? 'button' : undefined}
						tabIndex={isInteractive && !excludeFromTabOrder ? 0 : -1}
						aria-label={isInteractive ? resolvedAriaLabel : undefined}
					>
						<Bizy state={state} size={size} animations={animations} />
					</motion.div>
				</motion.div>
				<AnimatePresence>
					{isGamePromptOpen && holdEnabled && (
						<motion.div
							key="bizy-game-invite"
							initial={{ opacity: 0, x: '-50%', y: 6, scale: 0.96 }}
							animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
							exit={{ opacity: 0, x: '-50%', y: 4, scale: 0.96 }}
							transition={{ duration: 0.18 }}
							style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 10px)', zIndex: 2, pointerEvents: 'auto' }}
						>
							<BizyGameInvite onPlay={handleGameInvitePlay} onDismiss={handleGameInviteDismiss} />
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>

			{isGameOpen && holdEnabled && (
				<Suspense fallback={null}>
					<LazyBizySwarmGame onClose={closeGame} />
				</Suspense>
			)}
		</>
	);
}
